import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Building2, Users, FileText, CheckCircle, XCircle, Clock, 
  ChevronRight, Eye, Search, Filter, TrendingUp, Award,
  BarChart3, UserCheck, UserX, RefreshCw, DollarSign, Tag
} from 'lucide-react';
import CompanyPortalPricing from './CompanyPortalPricing';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CompanyAssessmentAdmin = () => {
  const [activeSubTab, setActiveSubTab] = useState('companies'); // companies, pricing
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data.companies);
    } catch (err) {
      toast.error('Failed to fetch companies');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyDetail = async (companyId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/companies/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedCompany(response.data);
      setSelectedAssessment(null);
      setCandidates([]);
    } catch (err) {
      toast.error('Failed to fetch company details');
    }
  };

  const fetchAssessmentDetail = async (companyId, assessmentId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/companies/${companyId}/assessments/${assessmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedAssessment(response.data);
      setCandidates(response.data.candidates || []);
    } catch (err) {
      toast.error('Failed to fetch assessment details');
    }
  };

  const toggleCompanyStatus = async (companyId, currentStatus) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/companies/${companyId}/status`, 
        { is_active: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Company ${!currentStatus ? 'activated' : 'deactivated'}`);
      fetchCompanies();
    } catch (err) {
      toast.error('Failed to update company status');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      invited: 'bg-blue-100 text-blue-700',
      started: 'bg-yellow-100 text-yellow-700',
      completed: 'bg-green-100 text-green-700',
      expired: 'bg-gray-100 text-gray-700'
    };
    return badges[status] || 'bg-gray-100 text-gray-700';
  };

  const getSelectionBadge = (status) => {
    const badges = {
      selected: 'bg-green-100 text-green-700 border-green-300',
      next_round: 'bg-blue-100 text-blue-700 border-blue-300',
      rejected: 'bg-red-100 text-red-700 border-red-300',
      pending: 'bg-gray-100 text-gray-600 border-gray-300'
    };
    return badges[status] || 'bg-gray-100 text-gray-600 border-gray-300';
  };

  const filteredCompanies = companies.filter(c => 
    c.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredCandidates = candidates.filter(c => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'passed') return c.passed;
    if (filterStatus === 'failed') return c.status === 'completed' && !c.passed;
    if (filterStatus === 'selected') return c.selection_status === 'selected';
    if (filterStatus === 'rejected') return c.selection_status === 'rejected';
    return c.status === filterStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Building2 className="text-purple-600" size={28} />
            Company Portal
          </h2>
          <p className="text-gray-600 mt-1">Manage companies, assessments, and pricing</p>
        </div>
        <button
          onClick={fetchCompanies}
          className="p-2 bg-purple-100 text-purple-600 rounded-lg hover:bg-purple-200 transition-colors"
        >
          <RefreshCw size={20} />
        </button>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveSubTab('companies')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeSubTab === 'companies'
              ? 'text-purple-600 border-purple-600'
              : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <Building2 size={18} />
            Companies & Assessments
          </div>
        </button>
        <button
          onClick={() => setActiveSubTab('pricing')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeSubTab === 'pricing'
              ? 'text-purple-600 border-purple-600'
              : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
          data-testid="pricing-tab"
        >
          <div className="flex items-center gap-2">
            <DollarSign size={18} />
            Pricing
          </div>
        </button>
      </div>

      {/* Pricing Tab */}
      {activeSubTab === 'pricing' && <CompanyPortalPricing />}

      {/* Companies Tab */}
      {activeSubTab === 'companies' && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
              <Building2 size={24} className="opacity-80 mb-2" />
              <p className="text-3xl font-bold">{companies.length}</p>
              <p className="text-purple-200 text-sm">Total Companies</p>
            </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <FileText size={24} className="opacity-80 mb-2" />
          <p className="text-3xl font-bold">{companies.reduce((sum, c) => sum + (c.assessments_count || 0), 0)}</p>
          <p className="text-blue-200 text-sm">Total Assessments</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
          <Users size={24} className="opacity-80 mb-2" />
          <p className="text-3xl font-bold">{companies.reduce((sum, c) => sum + (c.total_candidates || 0), 0)}</p>
          <p className="text-green-200 text-sm">Total Candidates</p>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-4 text-white">
          <CheckCircle size={24} className="opacity-80 mb-2" />
          <p className="text-3xl font-bold">{companies.filter(c => c.is_active !== false).length}</p>
          <p className="text-amber-200 text-sm">Active Companies</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Companies List */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h3 className="font-semibold text-gray-800 mb-3">Registered Companies</h3>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search companies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm"
              />
            </div>
          </div>
          <div className="max-h-[500px] overflow-y-auto">
            {filteredCompanies.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No companies found</p>
            ) : (
              filteredCompanies.map((company) => (
                <div
                  key={company.id}
                  onClick={() => fetchCompanyDetail(company.id)}
                  className={`p-4 border-b cursor-pointer hover:bg-purple-50 transition-colors ${
                    selectedCompany?.company?.id === company.id ? 'bg-purple-50 border-l-4 border-l-purple-500' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">{company.company_name}</p>
                      <p className="text-xs text-gray-500">{company.email}</p>
                    </div>
                    <ChevronRight size={16} className="text-gray-400" />
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-xs">
                    <span className="flex items-center gap-1 text-blue-600">
                      <FileText size={12} /> {company.assessments_count || 0}
                    </span>
                    <span className="flex items-center gap-1 text-green-600">
                      <Users size={12} /> {company.total_candidates || 0}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full ${company.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {company.is_active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Company Detail / Assessments */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h3 className="font-semibold text-gray-800">
              {selectedCompany ? `${selectedCompany.company.company_name}'s Assessments` : 'Select a Company'}
            </h3>
          </div>
          <div className="max-h-[500px] overflow-y-auto">
            {!selectedCompany ? (
              <p className="text-center text-gray-500 py-8">Select a company to view assessments</p>
            ) : selectedCompany.assessments.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No assessments created</p>
            ) : (
              selectedCompany.assessments.map((assessment) => (
                <div
                  key={assessment.id}
                  onClick={() => fetchAssessmentDetail(selectedCompany.company.id, assessment.id)}
                  className={`p-4 border-b cursor-pointer hover:bg-blue-50 transition-colors ${
                    selectedAssessment?.assessment?.id === assessment.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-gray-900">{assessment.title}</p>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${assessment.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {assessment.is_active ? 'Active' : 'Closed'}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-600">
                    <span>{assessment.duration_minutes} mins</span>
                    <span>{assessment.total_mcqs} MCQs</span>
                    <span>{assessment.total_coding_problems} Coding</span>
                    <span>{assessment.total_points} pts</span>
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-xs">
                    <span className="text-blue-600">{assessment.candidates_invited || 0} invited</span>
                    <span className="text-green-600">{assessment.candidates_completed || 0} completed</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Candidates */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-800">
                {selectedAssessment ? 'Candidates' : 'Select an Assessment'}
              </h3>
              {selectedAssessment && (
                <span className="text-sm text-gray-500">{filteredCandidates.length} candidates</span>
              )}
            </div>
            {selectedAssessment && (
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm"
              >
                <option value="all">All Candidates</option>
                <option value="completed">Completed</option>
                <option value="passed">Passed</option>
                <option value="failed">Failed</option>
                <option value="selected">Selected</option>
                <option value="rejected">Rejected</option>
              </select>
            )}
          </div>
          <div className="max-h-[450px] overflow-y-auto">
            {!selectedAssessment ? (
              <p className="text-center text-gray-500 py-8">Select an assessment to view candidates</p>
            ) : filteredCandidates.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No candidates found</p>
            ) : (
              filteredCandidates.map((candidate) => (
                <div key={candidate.id} className="p-4 border-b hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">{candidate.candidate_name || candidate.email}</p>
                      <p className="text-xs text-gray-500">{candidate.email}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs border ${getSelectionBadge(candidate.selection_status)}`}>
                      {candidate.selection_status || 'Pending'}
                    </span>
                  </div>
                  {candidate.status === 'completed' && (
                    <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                      <div className="bg-gray-100 rounded p-2 text-center">
                        <p className="text-gray-600">Score</p>
                        <p className="font-bold text-gray-900">{candidate.score}/{selectedAssessment.assessment.total_points}</p>
                      </div>
                      <div className="bg-gray-100 rounded p-2 text-center">
                        <p className="text-gray-600">Percentage</p>
                        <p className="font-bold text-gray-900">{candidate.percentage || 0}%</p>
                      </div>
                      <div className={`rounded p-2 text-center ${candidate.passed ? 'bg-green-100' : 'bg-red-100'}`}>
                        <p className={candidate.passed ? 'text-green-600' : 'text-red-600'}>Result</p>
                        <p className={`font-bold ${candidate.passed ? 'text-green-700' : 'text-red-700'}`}>
                          {candidate.passed ? 'PASS' : 'FAIL'}
                        </p>
                      </div>
                    </div>
                  )}
                  {candidate.status !== 'completed' && (
                    <span className={`mt-2 inline-block px-2 py-1 rounded-full text-xs ${getStatusBadge(candidate.status)}`}>
                      {candidate.status}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default CompanyAssessmentAdmin;
